import { type ListParams, MAX_LIST_PAGE_SIZE, parseListParams, toSearchParams } from '@tnn/core'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

/**
 * List state lives in the URL (page, sort, search, filters), so a view can
 * be bookmarked, shared and restored with the browser's Back button.
 */
export function useListParams(defaults: Partial<ListParams> = {}) {
  const [searchParams, setSearchParams] = useSearchParams()

  const params = useMemo<ListParams>(() => {
    const parsed = parseListParams(searchParams)
    return {
      ...parsed,
      sort: parsed.sort ?? defaults.sort,
      pageSize: Math.min(searchParams.has('pageSize') ? parsed.pageSize : (defaults.pageSize ?? parsed.pageSize), MAX_LIST_PAGE_SIZE),
      filters: { ...(defaults.filters ?? {}), ...parsed.filters },
    }
  }, [searchParams, defaults.sort, defaults.pageSize, defaults.filters])

  const update = useCallback(
    (patch: Partial<ListParams>) => {
      const next: ListParams = { ...params, ...patch }
      if (!('page' in patch)) next.page = 1
      setSearchParams(toSearchParams(next), { replace: true })
    },
    [params, setSearchParams],
  )

  /** The person picked a sort (it is in the URL); otherwise the list shows its default order */
  const sortChosen = searchParams.has('sort')

  return [params, update, sortChosen] as const
}
